import React from 'react';
import { motion } from 'framer-motion';
import Image from '../../../components/AppImage';
import Icon from '../../../components/AppIcon';

const InventoryManagement = () => {
  const features = [
  {
    icon: "Package",
    title: "Real-Time Multi-Store Tracking",
    description: "Monitor inventory levels across all your locations in real-time with instant synchronization and automatic updates."
  },
  {
    icon: "Barcode",
    title: "Barcode Scanning Integration",
    description: "Seamlessly scan products for quick checkout and inventory updates with support for multiple barcode formats."
  },
  {
    icon: "Bell",
    title: "Low Stock Alert System",
    description: "Receive automatic notifications when inventory levels drop below your defined thresholds to prevent stockouts."
  },
  {
    icon: "RefreshCw",
    title: "Automatic Reorder Points",
    description: "Set intelligent reorder points that trigger purchase orders automatically based on sales velocity and lead times."
  }];


  return (
    <section className="py-16 md:py-20 lg:py-24 bg-background">
      <div className="container mx-auto px-6 lg:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12 lg:gap-16 items-center">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}>

            <div className="inline-flex items-center px-4 py-2 rounded-full bg-primary/10 mb-4 md:mb-6">
              <Icon name="Package" size={18} color="var(--color-primary)" strokeWidth={2} />
              <span className="ml-2 font-body text-sm font-medium text-primary">Inventory Management</span>
            </div>
            
            <h2 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-foreground mb-4 md:mb-6">
              Smart Inventory Control at Your Fingertips
            </h2>
            
            <p className="font-body text-base md:text-lg text-muted-foreground mb-6 md:mb-8">
              Take complete control of your inventory with advanced tracking, automated alerts, and real-time synchronization across all your retail locations.
            </p>
            
            <div className="space-y-4 md:space-y-6">
              {features?.map((feature, index) =>
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="flex items-start space-x-4">

                  <div className="flex-shrink-0 w-10 h-10 md:w-12 md:h-12 rounded-lg bg-primary/10 flex items-center justify-center">
                    <Icon name={feature?.icon} size={20} color="var(--color-primary)" strokeWidth={2} />
                  </div>
                  <div>
                    <h3 className="font-heading text-base md:text-lg font-semibold text-foreground mb-1 md:mb-2">
                      {feature?.title}
                    </h3>
                    <p className="font-body text-sm md:text-base text-muted-foreground">
                      {feature?.description}
                    </p>
                  </div>
                </motion.div>
              )}
            </div>
          </motion.div>
          
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="relative">

            <div className="relative rounded-2xl overflow-hidden shadow-purple-lg">
              <Image
                src="/cloud-app-screenshot/add-product-light.png"
                alt="Cultaz inventory management interface showing product management and stock tracking"
                className="w-full h-64 md:h-80 lg:h-96 object-cover" />

              <div className="absolute inset-0 bg-gradient-to-t from-primary/20 to-transparent"></div>
            </div>
            
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.3 }}
              className="absolute -bottom-4 -right-4 md:-bottom-6 md:-right-6 bg-card rounded-xl shadow-purple-md p-4 md:p-6 border border-border">

              <div className="flex items-center space-x-3 md:space-x-4">
                <div className="w-12 h-12 md:w-16 md:h-16 rounded-lg bg-success/10 flex items-center justify-center">
                  <Icon name="TrendingUp" size={24} color="var(--color-success)" strokeWidth={2} />
                </div>
                <div>
                  <p className="font-body text-xs md:text-sm text-muted-foreground">Stock Accuracy</p>
                  <p className="font-heading text-xl md:text-2xl font-bold text-foreground">99.8%</p>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>);


};

export default InventoryManagement;